import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import useUpgradeBanner from '../../UpgradeBanner/useUpgradeBanner';
import UpgradeBanner from '../../UpgradeBanner';
import LayoutSettings from '../utils/LayoutSettingsStorage';
import GlobalTheme from '../../../theme';

const BANNER_Z_INDEX = Number(GlobalTheme.mixins.banner.zIndex);
const BANNER_HEIGHT = Number(GlobalTheme.mixins.banner.height);

const useStyles = makeStyles()(theme => ({
  root: {
    opacity: 0,
    height: 0,
    visibility: 'hidden',
    transition: theme.transitions.create(['opacity', 'visibility', 'height'], {
      easing: theme.transitions.easing.easeIn,
      duration: theme.transitions.duration.standard,
    }),
  },
  open: {
    opacity: 1,
    visibility: 'visible',
  },
}));

const UpgradeBannerWithHook = React.forwardRef<HTMLElement, any>(
  (props, ref) => {
    const [showUpgradeBanner] = useUpgradeBanner();
    return showUpgradeBanner ? <UpgradeBanner {...props} ref={ref} /> : null;
  }
);

/**
 * @description This hook will use the cached bannerHeight if it exists, otherwise given a list of refs
 * it will calculate the max height of the refs and cache it.
 */
const useCachedHeight = (refs: Array<React.RefObject<HTMLElement>>) => {
  const cachedHeight =
    LayoutSettings.getCachedLayoutSettings()?.get('bannerHeight');
  const [height, setHeight] = useState<number>(
    cachedHeight ? Number(cachedHeight) : 0
  );

  useEffect(() => {
    const updateHeight = () => {
      const maxHeight = Math.max(
        ...refs.map(ref => ref.current?.clientHeight || 0)
      );
      if (maxHeight !== height) {
        LayoutSettings.updateCachedLayoutSettings({ bannerHeight: maxHeight });
        setHeight(maxHeight);
      }
    };

    updateHeight();
  }, [refs, height]);

  return { height };
};

/**
 * @description A container for all banners that should be displayed in the UI.
 */
export default function Banner() {
  const { classes, cx } = useStyles();
  const showUpgradeBanner = useUpgradeBanner()[0];
  const [open, setOpen] = useState<boolean>(showUpgradeBanner);

  const upgradeBannerRef = useRef<HTMLDivElement>(null);
  const { height } = useCachedHeight([upgradeBannerRef]);

  useEffect(() => {
    setOpen(showUpgradeBanner);
  }, [showUpgradeBanner]);

  return (
    <div
      suppressHydrationWarning
      className={cx(classes.root, { [classes.open]: open })}
      style={{ height: open ? height || BANNER_HEIGHT : 0 }}
    >
      <section style={{ position: 'relative' }}>
        {/* @todo -- remove these styles and implement onto the banner once this layout is live */}
        <UpgradeBannerWithHook
          ref={upgradeBannerRef}
          style={{ position: 'absolute', zIndex: BANNER_Z_INDEX }}
        />
      </section>
    </div>
  );
}
